import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';

import _cloneDeep from 'lodash/cloneDeep';
import _compact from 'lodash/compact';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

import i18n from './messages.json';
import { withContext } from './NotificationContext';
import { CONSTANTS } from '../../utils/common';

const MAX_TOKENS = CONSTANTS.max_token_num;

const splitInput = (raw) =>
  String(raw || '')
    .split(/[\s,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const truncateToken = (token) =>
  token.length <= 22 ? token : `${token.slice(0, 14)}…${token.slice(-6)}`;

const AddTokensBox = (props) => {
  const { show, onHide, onDone, context } = props;
  const { currentMessage, setStateAsync } = context;

  const [tokens, setTokens] = useState([]);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    const existing = _compact(_cloneDeep(currentMessage.targeting.selected) || []);

    setTokens(existing);
    setDraft('');
    // focus after render
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  }, [show, currentMessage.targeting.selected]);

  const duplicateSet = useMemo(() => {
    const seen = new Set();
    const dups = new Set();

    tokens.forEach((t) => {
      if (seen.has(t)) dups.add(t);
      else seen.add(t);
    });
    return dups;
  }, [tokens]);

  const isAtCapacity = tokens.length >= MAX_TOKENS;
  const hasDuplicates = duplicateSet.size > 0;

  const commitDraft = useCallback(() => {
    const next = splitInput(draft);

    if (next.length === 0) return;

    setTokens((prev) => {
      const combined = [...prev];

      for (const t of next) {
        if (combined.length >= MAX_TOKENS) break;
        if (combined.includes(t)) continue;
        combined.push(t);
      }
      return combined;
    });
    setDraft('');
  }, [draft]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      commitDraft();
    } else if (e.key === 'Backspace' && draft === '' && tokens.length > 0) {
      e.preventDefault();
      setTokens((prev) => prev.slice(0, -1));
    }
  }, [commitDraft, draft, tokens.length]);

  const handlePaste = useCallback((e) => {
    const text = e.clipboardData.getData('text');

    if (!/[\s,;\n]/.test(text)) return;
    e.preventDefault();
    const parts = splitInput(`${draft} ${text}`);

    setTokens((prev) => {
      const combined = [...prev];

      for (const t of parts) {
        if (combined.length >= MAX_TOKENS) break;
        if (combined.includes(t)) continue;
        combined.push(t);
      }
      return combined;
    });
    setDraft('');
  }, [draft]);

  const removeAt = useCallback((idx) => {
    setTokens((prev) => prev.filter((_t, i) => i !== idx));
  }, []);

  const handleClearAll = useCallback(() => {
    setTokens([]);
    setDraft('');
    inputRef.current && inputRef.current.focus();
  }, []);

  const handleDone = useCallback(async () => {
    if (hasDuplicates) return;
    const cleaned = _compact(tokens);

    if (draft.trim().length > 0) {
      const extras = splitInput(draft);

      extras.forEach((t) => {
        if (cleaned.length < MAX_TOKENS && !cleaned.includes(t)) cleaned.push(t);
      });
    }

    await setStateAsync({ tokens: cleaned });
    onDone();
  }, [hasDuplicates, tokens, draft, setStateAsync, onDone]);

  return (
    <Modal
      aria-labelledby='add-tokens-title'
      centered
      backdrop='static'
      onHide={onHide}
      show={show}
      size='md'
      dialogClassName='add-token-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title id='add-tokens-title'>
          {i18n.ink.notification.addTokenBox.title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className='token-input__hint'>
          {i18n.ink.notification.addTokenBox.hint}
        </p>

        <div
          className={`token-input ${isAtCapacity ? 'token-input--full' : ''}`}
          onClick={() => inputRef.current && inputRef.current.focus()}
        >
          <div className='token-input__chips'>
            {tokens.map((token, idx) => {
              const dup = duplicateSet.has(token);

              return (
                <span
                  key={`${token}-${idx}`}
                  className={`token-chip ${dup ? 'token-chip--duplicate' : ''}`}
                  title={token}
                >
                  <span className='token-chip__value'>{truncateToken(token)}</span>
                  <button
                    type='button'
                    className='token-chip__remove'
                    aria-label={`${i18n.ink.notification.addTokenBox.removeAria}: ${token}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeAt(idx);
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </span>
              );
            })}
            <input
              ref={inputRef}
              type='text'
              className='token-input__field'
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={commitDraft}
              placeholder={
                tokens.length === 0
                  ? i18n.ink.notification.addTokenBox.placeholder
                  : ''
              }
              disabled={isAtCapacity}
            />
          </div>
        </div>

        <div className='token-input__footer'>
          <Form.Text className='text-muted'>
            {tokens.length} / {MAX_TOKENS}
          </Form.Text>
          <Button
            variant='link'
            size='sm'
            className='token-input__clear-btn'
            onClick={handleClearAll}
            disabled={tokens.length === 0 && draft === ''}
          >
            {i18n.ink.notification.addTokenBox.clearAll}
          </Button>
        </div>

        {hasDuplicates && (
          <div className='token-input__error'>
            {i18n.ink.notification.addTokenBox.duplicatedToken}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='light' id='on-hide-add-tokens-box' onClick={onHide}>
          {i18n.ink.notification.buttons.cancel}
        </Button>
        <Button
          variant='primary'
          id='on-done-add-tokens-box'
          onClick={handleDone}
          disabled={hasDuplicates}
        >
          {i18n.ink.notification.buttons.done}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default withContext(AddTokensBox);
